import { useMemo } from 'react';
import { StatCard, StatCardGrid } from '../../../components/common';
import { fmtCurrency } from '../../../utils/formatNumber';
import { formatDate } from '../../../utils/dateUtils';
import styles from '../Reports.module.css';

export function buildBankGroups(data) {
  const allRows      = data?.rows              || [];
  const bankAccounts = data?.bankAccounts      || [];
  const bankOpening  = data?.bankOpeningBalances || {};

  return bankAccounts.map((account) => {
    const rows  = allRows.filter((r) => String(r.bankAccountId) === String(account.id));
    const ob    = Number(bankOpening[account.id] || 0);
    let running = ob;
    const rowsWithBal = rows.map((r) => {
      running += Number(r.debit || 0) - Number(r.credit || 0);
      return { ...r, runBal: running };
    });
    const cb      = rowsWithBal.length ? rowsWithBal[rowsWithBal.length - 1].runBal : ob;
    const totalDr = rows.reduce((s, r) => s + Number(r.debit  || 0), 0);
    const totalCr = rows.reduce((s, r) => s + Number(r.credit || 0), 0);
    return { account, rows: rowsWithBal, openingBalance: ob, closingBalance: cb, totalDr, totalCr };
  });
}

function BankCard({ account, rows, openingBalance, closingBalance, totalDr, totalCr }) {
  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      {/* Bank heading bar */}
      <div style={{
        padding: '10px 16px',
        background: 'var(--color-primary)',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{account.name}</span>
      </div>

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Voucher No</th>
              <th>Payee / Member</th>
              <th>Chq./Ref.No.</th>
              <th>Narration</th>
              <th className={styles.amtRight}>Dr (₹)</th>
              <th className={styles.amtRight}>Cr (₹)</th>
              <th className={styles.amtRight}>Balance (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ background: 'var(--color-bg)' }}>
              <td colSpan={7} style={{ fontWeight: 600, fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                Opening Balance
              </td>
              <td className={styles.amtRight} style={{ fontWeight: 700 }}>{fmtCurrency(openingBalance)}</td>
            </tr>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="empty-state" style={{ padding: '10px 16px' }}>
                  No transactions in this period.
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDate(r.date)}</td>
                <td><code>{r.voucherNo}</code></td>
                <td>{r.payeeName || '—'}</td>
                <td style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>{r.referenceNo || '—'}</td>
                <td>{r.narration}</td>
                <td className={styles.amtRight}>{r.debit  ? fmtCurrency(r.debit)  : '—'}</td>
                <td className={styles.amtRight}>{r.credit ? fmtCurrency(r.credit) : '—'}</td>
                <td className={styles.amtRight}><strong>{fmtCurrency(r.runBal)}</strong></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={5}><strong>Closing Balance</strong></td>
              <td className={styles.amtRight}><strong>{fmtCurrency(totalDr)}</strong></td>
              <td className={styles.amtRight}><strong>{fmtCurrency(totalCr)}</strong></td>
              <td className={styles.amtRight}><strong>{fmtCurrency(closingBalance)}</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default function BankBookReport({ data, bankAccountId }) {
  const bankGroups = useMemo(() => buildBankGroups(data), [data]);

  if (!data) return null;

  const bankAccounts  = data.bankAccounts || [];
  const selectedBank  = bankAccountId
    ? bankAccounts.find((a) => String(a.id) === String(bankAccountId))
    : null;

  const displayGroups = selectedBank
    ? bankGroups.filter((g) => String(g.account.id) === String(bankAccountId))
    : bankGroups;

  const singleGroup = displayGroups[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {selectedBank && singleGroup ? (
        <StatCardGrid cols={3}>
          <StatCard label="Opening Balance" value={fmtCurrency(singleGroup.openingBalance)} />
          <StatCard label="Total Debits"    value={fmtCurrency(singleGroup.totalDr)} />
          <StatCard label="Total Credits"   value={fmtCurrency(singleGroup.totalCr)} />
        </StatCardGrid>
      ) : (
        <StatCardGrid cols={2}>
          <StatCard label="Total Opening Balance" value={fmtCurrency(data.openingBalance)} />
          <StatCard label="Total Closing Balance" value={fmtCurrency(data.closingBalance)} valueColor="green" />
        </StatCardGrid>
      )}

      {displayGroups.map((g) => (
        <BankCard key={g.account.id} {...g} />
      ))}
    </div>
  );
}
